import {AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, QueryList, ViewChild, ViewChildren} from '@angular/core';
import {MatMenuTrigger} from '@angular/material/menu';
import {ActivatedRoute} from '@angular/router';
import {HeaderIdentifier} from '@application/headers/header-identifier.enum';
import {RouteUtils} from '@application/helper/routing/route-utils';
import {Equipment} from '@domain/machine/equipment';
import {ProductionSchedulePhase} from '@domain/production-schedule/production-schedule-phase.enum';
import {ProductionScheduleStatus} from '@domain/production-schedule/production-schedule-status.enum';
import {MachineOverviewList} from '@presentation/pages/machine-overview/machine-overview-list';
import {AssertionUtils, DOMUtils, InputChipComponent, LocalStorageService, SaveTypeForProductionSchedule, skeletonViewAnimation} from '@vdw/angular-component-library';
import {Subscription, takeUntil, timer} from 'rxjs';
import {BaseAddProductionSchedulePageComponent, ProductionScheduleInitialPreparationsFormGroup} from '../base-add-production-schedule-page.component';
import {MenuItemsCanShowProperties} from './menu-items-can-show-properties.interface';
import {MenuItemsLoadingProperties} from './menu-items-loading-properties.interface';
import {PathContentSettingsDialogData} from './path-content-settings-dialog-data.interface';
import {PathContentSettingsDialogComponent} from './path-content-settings-dialog.component';
import {ProductionScheduleClickEvent} from './production-schedule-click-event.enum';
import {ProductionScheduleMenuItemChip} from './production-schedule-menu-item-chip.interface';

@Component({
  selector: 'app-production-schedule-menu-items',
  templateUrl: './production-schedule-menu-items.component.html',
  styleUrls: ['./production-schedule-menu-items.component.scss'],
  animations: [skeletonViewAnimation('.button-skeleton-wrapper, text-skeleton-wrapper')]
})
export class ProductionScheduleMenuItemsComponent extends BaseAddProductionSchedulePageComponent implements OnInit, AfterViewInit {
  @ViewChildren('chips') public inputChipComponents: QueryList<InputChipComponent>;
  @ViewChild('verifyMenuTrigger', {read: MatMenuTrigger}) public verifyMenuTrigger: MatMenuTrigger;
  @ViewChild('saveMenuTrigger', {read: MatMenuTrigger}) public saveMenuTrigger: MatMenuTrigger;
  @ViewChild('chipsDivider') public chipsDividerElement: ElementRef;
  @ViewChild('chipContainer') public chipContainerElement: ElementRef;
  @ViewChild('menuItems') public menuItemsElement: ElementRef;
  @ViewChild('phaseDivider') public phaseDividerElement: ElementRef;
  @ViewChild('productionScheduleProgress') public productionScheduleProgressElement: ElementRef;
  @ViewChild('actions') public actionsElement: ElementRef;

  @Input() public loadingProperties: MenuItemsLoadingProperties;
  @Input() public productionScheduleInitialPreparationsForm: ProductionScheduleInitialPreparationsFormGroup;
  @Input() public productionSchedulePhase: ProductionSchedulePhase;
  @Input() public customErrorMessage: string;
  @Input() public canShowProperties: MenuItemsCanShowProperties;
  @Input() public pathContentSettingsDialogData: PathContentSettingsDialogData;
  @Input() public productionScheduleStatus: ProductionScheduleStatus;
  @Input() public areAllFieldsDisabled: boolean = false;
  @Output() public clickEvent = new EventEmitter<ProductionScheduleClickEvent>();
  @Output() public productionSchedulePhaseClicked = new EventEmitter<ProductionSchedulePhase>();
  @Output() public pathContentSettingsDialogDataChanged: EventEmitter<PathContentSettingsDialogData> = new EventEmitter();

  public saveMenuTimer: Subscription;
  public chips: ProductionScheduleMenuItemChip[] = [];
  public canShowProductionScheduleProgress = true;

  public readonly SAVE_TYPE = SaveTypeForProductionSchedule.SAVE;
  public readonly SAVE_AND_CREATE_NEW_TYPE = SaveTypeForProductionSchedule.SAVE_AND_CREATE_NEW;
  public readonly SAVE_AND_GO_TO_PRESELECTION_TYPE = SaveTypeForProductionSchedule.SAVE_AND_GO_TO_PRESELECTION;
  public readonly SAVE_AND_QUIT_TYPE = SaveTypeForProductionSchedule.SAVE_AND_QUIT;
  public readonly VERIFY_TRANSLATION_KEY = 'PRODUCTION_ORDER.PHASES.VERIFY';
  public readonly INITIAL_DATA_NOT_LOADED_TRANSLATION_KEY = 'PRODUCTION_ORDER.INITIAL_DATA_LOADING';
  public readonly SAVE = 'GENERAL.ACTIONS.SAVE';
  public readonly SAVE_AND_CREATE_NEW = 'PRODUCTION_ORDER.SAVE_AND_CREATE_NEW';
  public readonly SAVE_AND_GO_TO_PRESELECTION = 'PRODUCTION_ORDER.SAVE_AND_GO_TO_PRESELECTION';
  public readonly SAVE_AND_QUIT = 'PRODUCTION_ORDER.SAVE_AND_QUIT';

  public readonly pathContentSettingsDialogComponent = PathContentSettingsDialogComponent;

  private saveButtonClicked: boolean;
  private defaultSaveType: SaveTypeForProductionSchedule;

  private readonly HEADER_IDENTIFIER = HeaderIdentifier.ADD_PRODUCTION_SCHEDULE;
  private readonly LOCAL_STORAGE_SAVE_KEY = 'productionScheduleSaveType';
  private readonly GAP_WIDTH_IN_PX = 16;

  public constructor(
    private readonly localStorage: LocalStorageService,
    activatedRoute: ActivatedRoute
  ) {
    super(activatedRoute);
  }

  public ngOnInit(): void {
    this.productionScheduleInitialPreparationsForm.valueChanges.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe(() => this.setHeaderChips());

    if (this.localStorage.get<SaveTypeForProductionSchedule>(`${this.LOCAL_STORAGE_SAVE_KEY}.${this.HEADER_IDENTIFIER}`)) {
      this.defaultSaveType = this.localStorage.get<SaveTypeForProductionSchedule>(`${this.LOCAL_STORAGE_SAVE_KEY}.${this.HEADER_IDENTIFIER}`);
    } else {
      this.defaultSaveType = SaveTypeForProductionSchedule.SAVE;
    }
  }

  public ngAfterViewInit(): void {
    DOMUtils.observeResize(this.menuItemsElement.nativeElement)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe(() => this.setMenuItemsVisibility());

    DOMUtils.observeResize(this.actionsElement.nativeElement)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe(() => {
        setTimeout(() => this.setMenuItemsVisibility());
      });
  }

  public isRaisedButton(): boolean {
    return this.productionSchedulePhase === ProductionSchedulePhase.BUILD ? false : true;
  }

  public getLabelForNextButton(): string {
    switch (this.productionSchedulePhase) {
      case ProductionSchedulePhase.CONFIGURE:
        return 'PRODUCTION_ORDER.NAVIGATION.GO_TO_BUILDER';
      case ProductionSchedulePhase.PRESELECTION:
        return 'PRODUCTION_ORDER.NAVIGATION.CREATE_PRODUCTION_ORDERS';
      case ProductionSchedulePhase.BUILD:
        return this.VERIFY_TRANSLATION_KEY;
      case ProductionSchedulePhase.VERIFY:
        return 'GENERAL.ACTIONS.CONFIRM';
      default:
        if (this.loadingProperties?.isLoading) {
          return 'PRODUCTION_ORDER.NAVIGATION.TO_BUILD';
        }

        return '';
    }
  }

  public getLabelForSaveButton(): string {
    switch (this.defaultSaveType) {
      case SaveTypeForProductionSchedule.SAVE:
        return this.SAVE;
      case SaveTypeForProductionSchedule.SAVE_AND_CREATE_NEW:
        return this.SAVE_AND_CREATE_NEW;
      case SaveTypeForProductionSchedule.SAVE_AND_GO_TO_PRESELECTION:
        return this.SAVE_AND_GO_TO_PRESELECTION;
      case SaveTypeForProductionSchedule.SAVE_AND_QUIT:
        return this.SAVE_AND_QUIT;
      default:
        return this.SAVE;
    }
  }

  public waitingForInitialZonesToLoad(): boolean {
    if (this.productionSchedulePhase !== ProductionSchedulePhase.CONFIGURE || this.canShowProperties?.canShowInvalidFormMessageError) {
      return false;
    }

    const loadProperties = this.loadingProperties;
    const initialZonesLoaded = !(loadProperties?.initialRestZoneLoaded && loadProperties?.initialFreeZoneLoaded && loadProperties?.initialPathZoneLoaded && loadProperties?.initialJuteZoneLoaded);

    return initialZonesLoaded && this.saveButtonClicked;
  }

  public canShowToPreselectionButton(): boolean {
    return (
      (this.productionSchedulePhase === ProductionSchedulePhase.CONFIGURE && !this.areAllFieldsDisabled) ||
      (!AssertionUtils.isNullOrUndefined(this.loadingProperties?.isLoading) && this.loadingProperties?.isLoading)
    );
  }

  public canShowSaveMenuTrigger(): boolean {
    return (
      (this.productionSchedulePhase === ProductionSchedulePhase.BUILD || this.productionSchedulePhase === ProductionSchedulePhase.VERIFY) &&
      (this.productionScheduleStatus === ProductionScheduleStatus.DRAFT ||
        this.productionScheduleStatus === ProductionScheduleStatus.VERIFIED ||
        this.productionScheduleStatus === ProductionScheduleStatus.VERIFIED_WITH_WARNINGS ||
        this.productionScheduleStatus === ProductionScheduleStatus.VERIFIED_WITH_ERRORS)
    );
  }

  public toPreselectionClicked(): void {
    this.clickEvent.emit(ProductionScheduleClickEvent.PRESELECTION);
  }

  public nextPhaseClicked(): void {
    if (this.productionSchedulePhase !== ProductionSchedulePhase.PRESELECTION) {
      this.saveButtonClicked = true;
    }

    this.clickEvent.emit(ProductionScheduleClickEvent.NEXT_PHASE);
  }

  public completeClicked(): void {
    this.clickEvent.emit(ProductionScheduleClickEvent.COMPLETE);
  }

  public decreateClicked(): void {
    this.clickEvent.emit(ProductionScheduleClickEvent.DECREATE);
  }

  public cancelClicked(): void {
    this.clickEvent.emit(ProductionScheduleClickEvent.CANCEL);
  }

  public settingsButtonClicked(): void {
    this.clickEvent.emit(ProductionScheduleClickEvent.SETTINGS);
  }

  public mouseEnterSaveButton(): void {
    if (this.saveMenuTimer) {
      this.saveMenuTimer.unsubscribe();
    }

    this.saveMenuTrigger.openMenu();
  }

  public mouseLeaveSaveButton(): void {
    this.saveMenuTimer = timer(500).subscribe(() => this.saveMenuTrigger.closeMenu());
  }

  public hasSaveMenuButton(): boolean {
    return !this.isDefaultSaveType(SaveTypeForProductionSchedule.SAVE);
  }

  public hasSaveAndGoToPreselectionMenuButton(): boolean {
    return !this.isDefaultSaveType(SaveTypeForProductionSchedule.SAVE_AND_GO_TO_PRESELECTION);
  }

  public hasSaveAndCreateNewMenuButton(): boolean {
    return !this.isDefaultSaveType(SaveTypeForProductionSchedule.SAVE_AND_CREATE_NEW);
  }

  public hasSaveAndQuitMenuButton(): boolean {
    return !this.isDefaultSaveType(SaveTypeForProductionSchedule.SAVE_AND_QUIT);
  }

  public onProductionSchedulePhaseClicked(event: ProductionSchedulePhase): void {
    this.productionSchedulePhaseClicked.emit(event);
  }

  private isDefaultSaveType(saveType: SaveTypeForProductionSchedule): boolean {
    return saveType === this.defaultSaveType;
  }

  public save(saveType?: SaveTypeForProductionSchedule): void {
    if (!AssertionUtils.isNullOrUndefined(saveType)) {
      this.defaultSaveType = saveType;
      this.localStorage.set(`${this.LOCAL_STORAGE_SAVE_KEY}.${this.HEADER_IDENTIFIER}`, saveType);
    }

    if (saveType === SaveTypeForProductionSchedule.SAVE_AND_QUIT) {
      this.clickEvent.emit(ProductionScheduleClickEvent.SAVE_AND_QUIT);
    } else if (saveType === SaveTypeForProductionSchedule.SAVE_AND_CREATE_NEW) {
      this.clickEvent.emit(ProductionScheduleClickEvent.SAVE_AND_CREATE_NEW);
    } else if (saveType === SaveTypeForProductionSchedule.SAVE_AND_GO_TO_PRESELECTION) {
      this.clickEvent.emit(ProductionScheduleClickEvent.SAVE_AND_GO_TO_PRESELECTION);
    } else if (!AssertionUtils.isNullOrUndefined(this.defaultSaveType)) {
      if (this.defaultSaveType === SaveTypeForProductionSchedule.SAVE) {
        this.clickEvent.emit(ProductionScheduleClickEvent.SAVE);
      } else if (this.defaultSaveType === SaveTypeForProductionSchedule.SAVE_AND_CREATE_NEW) {
        this.clickEvent.emit(ProductionScheduleClickEvent.SAVE_AND_CREATE_NEW);
      } else if (this.defaultSaveType === SaveTypeForProductionSchedule.SAVE_AND_GO_TO_PRESELECTION) {
        this.clickEvent.emit(ProductionScheduleClickEvent.SAVE_AND_GO_TO_PRESELECTION);
      } else if (this.defaultSaveType === SaveTypeForProductionSchedule.SAVE_AND_QUIT) {
        this.clickEvent.emit(ProductionScheduleClickEvent.SAVE_AND_QUIT);
      }
    } else {
      this.clickEvent.emit(ProductionScheduleClickEvent.SAVE);
    }
  }

  public getDialogData(): PathContentSettingsDialogData {
    return {
      pathContentPanelEnabled: this.pathContentSettingsDialogData?.pathContentPanelEnabled
    } as PathContentSettingsDialogData;
  }

  public onOptionChanged(event: PathContentSettingsDialogData): void {
    this.pathContentSettingsDialogDataChanged.emit(event);
  }

  private setHeaderChips(): void {
    this.chips = [];

    const machine = this.productionScheduleInitialPreparationsForm.value.machine;
    if (machine) {
      this.chips.push({
        value: machine.name,
        url: RouteUtils.paths.texState.editMachine.absolutePath
          .replace(':machineType', `${(machine instanceof Equipment || machine instanceof MachineOverviewList ? machine.machineType : machine.type)?.toLowerCase()}`)
          .replace(':id', `${machine.id}`)
      });
    }

    const machineQuality = this.productionScheduleInitialPreparationsForm.value.machineQuality;
    if (machineQuality) {
      this.chips.push({value: machineQuality.technicalNameWithVersion, url: RouteUtils.paths.texStyle.machineQuality.editMachineQuality.absolutePath.replace(':id', `${machineQuality.id}`)});
    }

    const creel = this.productionScheduleInitialPreparationsForm.value.creel;
    if (creel) {
      this.chips.push({value: creel.name, url: RouteUtils.paths.texStyle.creel.editCreel.absolutePath.replace(':id', `${creel.id}`)});
    }

    setTimeout(() => this.setMenuItemsVisibility());
  }

  private setMenuItemsVisibility(): void {
    const reversedInputChips = this.inputChipComponents.toArray().reverse();
    reversedInputChips.forEach((inputChipComponent: InputChipComponent) => (inputChipComponent.elementRef.nativeElement as HTMLElement).classList.remove('hidden'));
    (this.chipsDividerElement.nativeElement as HTMLElement).classList.remove('hidden');
    (this.productionScheduleProgressElement.nativeElement as HTMLElement).classList.remove('hidden');
    (this.phaseDividerElement.nativeElement as HTMLElement).classList.remove('hidden');

    reversedInputChips.forEach((inputChipComponent: InputChipComponent, index: number) => {
      const inputChipElement = inputChipComponent.elementRef.nativeElement;
      const chipContainerElement = (this.chipContainerElement.nativeElement as HTMLElement).offsetWidth;
      if (this.getAvailableWidth(true) < chipContainerElement) {
        (inputChipElement as HTMLElement).classList.add('hidden');

        if (index === reversedInputChips.length - 1) {
          (this.chipsDividerElement.nativeElement as HTMLElement).classList.add('hidden');
        }
      }
    });

    const productionScheduleProgressElement = this.productionScheduleProgressElement.nativeElement;
    if (this.getAvailableWidth(false) < (productionScheduleProgressElement as HTMLElement).offsetWidth) {
      (productionScheduleProgressElement as HTMLElement).classList.add('hidden');
      (this.phaseDividerElement.nativeElement as HTMLElement).classList.add('hidden');
    }
  }

  private getAvailableWidth(includeProgressWidth: boolean): number {
    const menuItemsWidth = this.getMenuItemsWidth();
    const actionButtonsWidth = (this.actionsElement.nativeElement as HTMLElement).offsetWidth;
    const productionScheduleProgressWidth = (this.productionScheduleProgressElement.nativeElement as HTMLElement).offsetWidth;
    const progressDividerWidth = (this.phaseDividerElement.nativeElement as HTMLElement).offsetWidth;

    return includeProgressWidth
      ? menuItemsWidth - actionButtonsWidth - productionScheduleProgressWidth - progressDividerWidth - 4 * this.GAP_WIDTH_IN_PX
      : menuItemsWidth - actionButtonsWidth - 2 * this.GAP_WIDTH_IN_PX;
  }

  private getMenuItemsWidth(): number {
    return (this.menuItemsElement.nativeElement as HTMLElement).offsetWidth;
  }
}
